import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination, Filters } from '@ui/src';
import { CurrencyItem, CurrencyStatus } from '../../types';
import { currenciesApi } from '../services/currencies';
import ROUTES from '../../../../routes';

export interface FormValues {
  status: CurrencyStatus;
  numericCode: string;
  characterCode: string;
  description: string;
  units: string;
  useReverseRateValue: boolean;
}

export interface CurrenciesListState {
  requestInProgress: boolean;
  values?: CurrencyItem[];
  errorsList?: string;
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  filters?: Filters;
  pagination?: Pagination;
  selectedCurrencies?: CurrencyItem[];
  selectedCurrencyIds?: string[];
  baseCurrency?: CurrencyItem;
  activeCount?: number;
  disableActivateBtn: boolean;
  disableInactivateBtn: boolean;
  formValues?: CurrencyItem;
}

const initialState: CurrenciesListState = {
  values: undefined,
  sort: undefined,
  requestInProgress: true,
  filters: undefined,
  disableActivateBtn: true,
  disableInactivateBtn: true,
};

export const currenciesListSlice = createSlice({
  name: 'currenciesList',
  initialState,
  reducers: {
    onSelectedRows: (state, action: PayloadAction<string[]>) => {
      state.selectedCurrencyIds = action.payload;

      const selectedCurrencies =
        state.values?.filter((item) => action.payload.includes(item.id)) || [];

      state.selectedCurrencies = selectedCurrencies;

      if (selectedCurrencies.length > 0) {
        const allActivated = selectedCurrencies.every((item) => item.active);
        const allDisabled = selectedCurrencies.every((item) => !item.active);

        if (allActivated) {
          state.disableInactivateBtn = false;
        } else if (allDisabled) {
          state.disableActivateBtn = false;
        } else {
          state.disableActivateBtn = false;
          state.disableInactivateBtn = false;
        }
      } else {
        state.disableActivateBtn = true;
        state.disableInactivateBtn = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        currenciesApi.endpoints.getEmployeesCurrencies.matchPending,
        (state) => {
          state.errorsList = undefined;
        },
      )
      .addMatcher(
        currenciesApi.endpoints.getEmployeesCurrencies.matchRejected,
        (state, action: any) => {
          state.values = undefined;
          state.pagination = undefined;

          if (action?.payload.data) {
            if (Array.isArray(action?.payload.data?.message)) {
              state.errorsList = action?.payload.data?.message
                ?.map((item: { value: string }) => item.value)
                .join('; ');
            } else {
              state.errorsList = action?.payload.data?.error;
            }
          }
        },
      )
      .addMatcher(
        currenciesApi.endpoints.getEmployeesCurrencies.matchFulfilled,
        (state, action: PayloadAction<Response<CurrencyItem>>) => {
          state.values = action.payload.data.edges.map((item) => ({
            ...item.node,
            url: generatePath(ROUTES.CURRENCIES_ITEM, {
              currencyId: item.node.id,
            }),
          }));

          state.pagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        currenciesApi.endpoints.updateCurrency.matchFulfilled,
        (state, action: PayloadAction<{ data: CurrencyItem }>) => {
          state.values = state.values?.map((item) => {
            const result = { ...item };

            if (result.id === action.payload.data.id) {
              result.decimals = action.payload.data.decimals;
            }

            return result;
          });
        },
      )
      .addMatcher(
        currenciesApi.endpoints.getEmployeesBaseCurrency.matchFulfilled,
        (state, action: PayloadAction<{ data: CurrencyItem }>) => {
          state.baseCurrency = action.payload.data;
        },
      )
      .addMatcher(
        currenciesApi.endpoints.toggleEmployeesCurrenciesActive.matchFulfilled,
        (state, action: PayloadAction<{ data: CurrencyItem }>) => {
          if (state.activeCount) {
            if (action.payload.data.active) {
              state.activeCount += 1;
            } else {
              state.activeCount -= 1;
            }
          } else {
            state.activeCount = 1;
          }

          state.values = state.values?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                url: generatePath(ROUTES.CURRENCIES_ITEM, {
                  currencyId: action.payload.data.id,
                }),
              };
            }

            return result;
          });
        },
      )
      .addMatcher(
        currenciesApi.endpoints.toggleCurrenciesStatus.matchFulfilled,
        (state) => {
          if (state.activeCount) {
            state.activeCount += state.selectedCurrencyIds?.length as number;
          } else {
            state.activeCount = state.selectedCurrencyIds?.length;
          }
        },
      )
      .addMatcher(
        currenciesApi.endpoints.toggleBaseCurrency.matchFulfilled,
        (state, action: PayloadAction<{ data: CurrencyItem }>) => {
          state.baseCurrency = action.payload.data;
        },
      )
      .addMatcher(
        currenciesApi.endpoints.getCountActiveCurrencies.matchFulfilled,
        (state, action: PayloadAction<Response<CurrencyItem>>) => {
          state.activeCount = action.payload.data.pageInfo.totalCount;
        },
      )
      .addMatcher(
        currenciesApi.endpoints.getEmployeesCurrency.matchFulfilled,
        (state, action: PayloadAction<{ data: CurrencyItem }>) => {
          state.formValues = action.payload.data;
        },
      )
      .addMatcher(
        currenciesApi.endpoints.toggleDefaultCreation.matchFulfilled,
        (state, action: any & PayloadAction<{ data: CurrencyItem }>) => {
          state.values = state.values?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                shouldCreateBalanceForClients:
                  action.meta.arg.originalArgs.body
                    .shouldCreateBalanceForClients,
                url: generatePath(ROUTES.CURRENCIES_ITEM, {
                  currencyId: action.payload.data.id,
                }),
              };
            }

            return result;
          });
        },
      );
  },
});

export const { onSelectedRows } = currenciesListSlice.actions;

export default currenciesListSlice.reducer;
